import { Loading } from '@src/common/Loading';
import { styled } from '@uhg-abyss/web/tools/styled';
import React from 'react';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useTinRegistrationStatus } from '@src/state/tinRegistrationStatus';
import { print } from './print';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { TinStatusV2 } from './TinStatusV2';

const TextStyleBold = styled('div', {
  fontWeight: 'bold',
});

const HeaderTinStyle = styled('div', {
  fontWeight: 'bold',
  paddingLeft: '5px',
});

const GrayText = styled('div', {
  color: '$gray6',
});

const CardHeader = styled('div', {
  display: 'inherit',
});

const MarginStyle = styled('div', {
  margin: '10px 10px 10px 0.825rem',
});

const DownloadPdf = styled('div', {
  fontWeight: 500,
  margin: '10px 10px 10px 0.825rem',
  display: 'inline-flex',
});

const CardWrapper = styled('div', {
  paddingBottom: '28px',
});

const StepDescriptions = styled('div', {
  display: 'flex',
  justifyContent: 'space-around',
  marginTop: '10px',
  fontSize: '14px',
});

const StepDescription = styled('div', {
  textAlign: 'center',
  flex: 1,
  color: '$gray6',
});

const IconContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'center',
  marginBottom: '20px',
});

const IconItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: 1,
});

const IconLabel = styled('div', {
  marginBottom: '8px',
  fontSize: '$sm',
  fontWeight: 500,
  textAlign: 'center',
  color: '$gray8',
});

export const TinStatus = () => {
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');

  // If feature toggle is enabled, use new flow with claimsAttribute, otherwise use old flow
  // we can remove this toggle once we go live in prod , also delete the v2 files
  return featureToggles.getRow('ENABLE_CLAIMS_ATTRIBUTE_FLOW')?.active ? (
    <TinStatusWithClaimsAttribute />
  ) : (
    <TinStatusV2 />
  );
};

const TinStatusWithClaimsAttribute = () => {
  const statusInfo = useTinRegistrationStatus.state();
  const {
    tin,
    registrationStatus,
    trackingNumber,
    reviewCompletedOn,
    submittedOn,
    claimsAttribute,
  } = statusInfo.tinRegistrationStatus;
  const { searchRegistrationStatusSaga } = statusInfo;

  const downloadPdf = () => {
    const fileName = `TinRegistrationStatus_${trackingNumber}`;
    const statusPage = document.getElementById('print-status');
    print.savePDF(statusPage, fileName);
  };

  return (
    <Loading isLoading={searchRegistrationStatusSaga.isLoading}>
      <CardWrapper id="print-status">
        <Card
          header={
            <CardHeader id="Status-CardTitle">
              Taxpayer Identification Number:{' '}
              <HeaderTinStyle id="Status-CardTitle-tin">{tin}</HeaderTinStyle>
            </CardHeader>
          }
        >
          <Grid>
            <Grid.Col span="19%">
              <Grid css={{ padding: '23px', paddingRight: '5px' }}>
                <MarginStyle>
                  <GrayText id="Status-ReferenceNumberText">
                    Reference number
                  </GrayText>
                  <TextStyleBold id="Status-ReferenceNumber">
                    {trackingNumber}
                  </TextStyleBold>
                </MarginStyle>
                <MarginStyle>
                  <GrayText id="Status-SubDateText">Submission date</GrayText>
                  <TextStyleBold id="Status-SubDate">
                    {submittedOn}
                  </TextStyleBold>
                </MarginStyle>
                <DownloadPdf>
                  <Button
                    variant="tertiary"
                    onClick={downloadPdf}
                    id="Status-downloadButton"
                    css={{ padding: '0px' }}
                    before={
                      <IconSymbol
                        icon="print"
                        size="20px"
                        id="Status-downloadIcon"
                      />
                    }
                  >
                    Print/Save Status as PDF
                  </Button>
                </DownloadPdf>
              </Grid>
            </Grid.Col>
            <Grid.Col span="3%">
              <Divider orientation="vertical" />
            </Grid.Col>
            <Grid.Col
              span="78%"
              css={{ paddingTop: '30px', paddingBottom: '30px' }}
            >
              <IconContainer>
                <IconItem>
                  <IconLabel>Submission</IconLabel>
                  <IconSymbol
                    icon="check_circle"
                    size="32px"
                    color="$success1"
                    variant="outlined"
                  />
                </IconItem>
                <IconItem>
                  <IconLabel>Submission Review</IconLabel>
                  {registrationStatus === 'PENDING' ? (
                    <IconSymbol icon="pending" size="32px" color="$info1" />
                  ) : (
                    <IconSymbol
                      icon="check_circle"
                      size="32px"
                      color="$success1"
                      variant="outlined"
                    />
                  )}
                </IconItem>
                <IconItem>
                  <IconLabel>
                    {claimsAttribute ? 'Claim Submitted' : 'Claim Submission'}
                  </IconLabel>
                  {claimsAttribute ||
                  (registrationStatus === 'ACCEPTED' && !claimsAttribute) ||
                  (registrationStatus === 'DENIED' && !claimsAttribute) ? (
                    <IconSymbol
                      icon="check_circle"
                      size="32px"
                      color="$success1"
                      variant="outlined"
                    />
                  ) : registrationStatus === 'RELEASED' ? (
                    <IconSymbol
                      icon="description"
                      size="32px"
                      color="$warning1"
                    />
                  ) : (
                    <IconSymbol icon="description" size="32px" color="$gray6" />
                  )}
                </IconItem>
                <IconItem>
                  <IconLabel>Verification</IconLabel>
                  {(registrationStatus === 'RELEASED' && claimsAttribute) ||
                  (registrationStatus === 'ACCEPTED' && claimsAttribute) ||
                  (registrationStatus === 'ACCEPTED' && !claimsAttribute) ? (
                    <IconSymbol icon="verified" size="32px" color="$success1" />
                  ) : registrationStatus === 'DENIED' ? (
                    <IconSymbol icon="cancel" size="32px" color="$error1" />
                  ) : (
                    <IconSymbol
                      icon="lock"
                      size="32px"
                      variant="outlined"
                      color="$gray6"
                    />
                  )}
                </IconItem>
              </IconContainer>
              <StepDescriptions>
                <StepDescription css={{ color: '$success1' }}>
                  Complete
                </StepDescription>
                <StepDescription
                  css={{
                    color:
                      registrationStatus === 'PENDING' ? '$info1' : '$success1',
                  }}
                >
                  {registrationStatus === 'PENDING' ? (
                    <React.Fragment>
                      In progress
                      <br />
                      <StepDescription css={{ color: '$gray6' }}>
                        You will receive an email once the review is complete
                      </StepDescription>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      Complete
                      <br />
                      <StepDescription css={{ color: '$gray6' }}>
                        Completed on {reviewCompletedOn || ''}
                      </StepDescription>
                    </React.Fragment>
                  )}
                </StepDescription>
                <StepDescription
                  css={{
                    color:
                      claimsAttribute ||
                      (registrationStatus === 'ACCEPTED' && !claimsAttribute) ||
                      (registrationStatus === 'DENIED' && !claimsAttribute)
                        ? '$success1'
                        : registrationStatus === 'RELEASED'
                        ? '$warning1'
                        : '$gray6',
                  }}
                >
                  {claimsAttribute ||
                  (registrationStatus === 'ACCEPTED' && !claimsAttribute) ||
                  (registrationStatus === 'DENIED' && !claimsAttribute)
                    ? 'Complete'
                    : 'Required'}
                </StepDescription>
                <StepDescription
                  css={{
                    color:
                      registrationStatus === 'DENIED'
                        ? '$error1'
                        : (registrationStatus === 'ACCEPTED' &&
                            claimsAttribute) ||
                          (registrationStatus === 'ACCEPTED' &&
                            !claimsAttribute)
                        ? '$success1'
                        : registrationStatus === 'RELEASED' && claimsAttribute
                        ? '$success1'
                        : '$gray6',
                  }}
                >
                  {registrationStatus === 'DENIED' ? (
                    <React.Fragment>
                      Denied
                      <br />
                      <StepDescription css={{ color: '$gray6' }}>
                        This TIN cannot be validated at this time
                      </StepDescription>
                    </React.Fragment>
                  ) : (registrationStatus === 'ACCEPTED' && claimsAttribute) ||
                    (registrationStatus === 'ACCEPTED' && !claimsAttribute) ? (
                    <React.Fragment>
                      Approved
                      <br />
                      <StepDescription css={{ color: '$gray6' }}>
                        The TIN has been successfully verified
                      </StepDescription>
                    </React.Fragment>
                  ) : registrationStatus === 'RELEASED' && claimsAttribute ? (
                    <React.Fragment>
                      Approved
                      <br />
                      <StepDescription css={{ color: '$gray6' }}>
                        The TIN has been successfully verified
                      </StepDescription>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>Pending</React.Fragment>
                  )}
                </StepDescription>
              </StepDescriptions>
            </Grid.Col>
          </Grid>
        </Card>
      </CardWrapper>
    </Loading>
  );
};
